/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import Table, { TableColumn } from '../../../components/table/Table';
import React, { createRef, useEffect, useState } from 'react';
import { DATE, formatDate } from '../../../utils/date';
import { Button } from 'reactstrap';
import { getFullName } from '../../../utils/user';
import SimpleTooltip from '../../../components/simple-tooltip';
import { naturalSort } from '../../../utils';
import { UserMessageModal } from '../../../components/message-modal/MessageModal';
import { DelegationStats, UserWithDelegation, UserWithStats } from '../../../api/initiative-stats';
import { BasicAlert } from '../../../components/alert/BasicAlert';
import { Row } from 'react-table';
import { SerializedError } from '@reduxjs/toolkit';
import { AdminUserDropdown, UserDropdownItem } from './AdminUserDropdown';
import { useHistory } from 'react-router-dom';
import { generateUrl } from '../../util';
import { ROUTES } from '../../../constants/routes';
import { useAppSelector } from '../../../reducers';
import { FeaturePermissions } from '../../../services/permissions/FeaturePermissions';

type UserRow = UserWithDelegation;
interface AdminUserTableParams {
  initiativeId: string;
  highlightUserId?: string | null;
  onUserClick?: (user: UserRow) => void;
  users: UserWithDelegation[];
  error?: {
    message: string;
    name: string;
  } | SerializedError;
}

const statusSortAsc = (prop: keyof DelegationStats) => (a: Row<UserRow>, b: Row<UserRow>): number => {
  const ap = a.original.delegationStats?.[prop] ?? -1;
  const bp = b.original.delegationStats?.[prop] ?? -1;

  if (ap < bp) return -1;
  if (ap > bp) return 1;
  return 0;
}

const highlightClass = 'row-highlight-blink';
const removeClass: React.MouseEventHandler<HTMLDivElement> = (e) => e.currentTarget.classList.remove(highlightClass);

export const AdminUserTable = (props: AdminUserTableParams) => {

  const { initiativeId, onUserClick, users, error, highlightUserId } = props;
  const [showUserMessage, setShowUserMessage] = useState<string | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const canAccessBulkDelegation = useAppSelector(FeaturePermissions.canAccessBulkDelegation);
  const history = useHistory();

  const userRowRef = createRef<HTMLDivElement>();
  useEffect(() => {
    if (userRowRef.current && highlightUserId) {
      userRowRef.current.scrollIntoView({
        behavior: 'smooth',
        inline: 'nearest',
      });
    }
  }, [userRowRef, highlightUserId]);

  if (error && error.message) {
    return <BasicAlert type={'danger'}>{error.message}</BasicAlert>
  }

  const handleToggle = (id: string) => {
    if (selectedId === id) {
      setSelectedId(undefined);
      return;
    }
    setSelectedId(id);
  }

  const getDropdownItems = (row: UserWithStats): UserDropdownItem[] => {
    const buttons = [
      {
        label: 'Message User',
        clickHandler: () => setShowUserMessage(row._id),
      },
    ];

    if (canAccessBulkDelegation) {
      buttons.push({
        label: 'Delegate to user',
        clickHandler: () =>
          history.push(generateUrl(ROUTES.SUBSIDIARY_USER_DELEGATION, { initiativeId, userId: row._id })),
      })
    }

    return buttons;
  };

  const columns: TableColumn<UserRow>[] = [
    {
      id: 'name',
      Header: 'Name',
      sortType: (a, b) => {
        return naturalSort(getFullName(a.original), getFullName(b.original));
      },
      accessor: (row) => {
        const isHighlighted = row._id === highlightUserId;
        return (
          <div className={isHighlighted ? highlightClass : ''}
            onMouseEnter={removeClass} ref={isHighlighted ? userRowRef : undefined}>
            {onUserClick ?
              <Button color={'link'}
                      onClick={() => onUserClick(row)} className={'text-truncate'}
                      style={{ display: 'block' }}>
                {getFullName(row)}
                {row.isStaff ? (
                  <SimpleTooltip text='Staff user. This user is not visible to non-staff.'>
                    <i className='ml-2 fa fa-user-ninja text-ThemeDangerMedium'/>
                  </SimpleTooltip>
                ) : null}
              </Button> :
              <span className={'text-truncate'} style={{ display: 'block' }}>
                {getFullName(row)}
              </span>
            }
          </div>
        )
      },
    },
    {
      Header: 'Last Online',
      accessor: (row) => formatDate(row.lastLogin ?? '', DATE.MONTH_YEAR_SHORT),
      sortType: (a, b) => naturalSort(a.original.lastLogin ?? '', b.original.lastLogin ?? ''),
    },
    {
      id: 'contributor',
      Header: () => <>Contributor</>,
      accessor: (r) => r.delegationStats?.contributor || '-',
      sortType: statusSortAsc('contributor'),
      cellProps: { style: { width: 20, } }
    },
    {
      id: 'updated',
      Header: () => <>Answered</>,
      accessor: (r) => r.delegationStats?.updated || '-',
      sortType: statusSortAsc('updated'),
      cellProps: { style: { width: 20, } }
    },
    {
      id: 'Verifier',
      Header: () => <>Verifier</>,
      accessor: (r) => r.delegationStats?.verifier || '-',
      sortType: statusSortAsc('verifier'),
      cellProps: { style: { width: 20, } }
    },
    {
      id: 'verified',
      Header: () => <>Verified</>,
      accessor: (r) => r.delegationStats?.verified || '-',
      sortType: statusSortAsc('verified'),
      cellProps: { style: { width: 20, } }
    },
    {
      id: 'actions',
      cellProps: {
        style: {
          borderRight: 0,
          textAlign: 'center',
          width: 40,
        }
      },
      Header: () => <></>,
      disableSortBy: true,
      accessor: (row) => {
        return (
          <AdminUserDropdown
            isOpen={row._id === selectedId}
            toggle={() => handleToggle(row._id)}
            dropdownItems={getDropdownItems(row)}
          />
        );
      },
    },
  ];

  return (
    <>
      <Table
        responsive
        defaultSortIcon={null}
        pageSize={10}
        columns={columns}
        data={users}
        sortBy={[{ id: 'name', desc: false }]}
        noData={<>No results</>}
      />
      {showUserMessage ? (
        <UserMessageModal users={users} userIds={[showUserMessage]} toggle={() => setShowUserMessage(undefined)} />
      ) : null}
    </>
  );
};
